
import React, { Suspense } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { AlertCircle, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { widgetRegistry } from '@/lib/dashboard/registry';
import { useAuthStore } from '@/lib/stores/auth';
import { RoleName } from '@/lib/constants/roles';

interface WidgetContainerProps {
  widgetId: string;
  role?: RoleName | null;
  onAction?: (action: string, data?: any) => void;
  size?: 'small' | 'medium' | 'large' | 'full';
  showHeader?: boolean;
  className?: string;
}

/**
 * Container component that handles permission checking and rendering of dashboard widgets
 */
export function WidgetContainer({
  widgetId,
  role,
  onAction,
  size = 'medium',
  showHeader = true,
  className = ''
}: WidgetContainerProps) {
  const { user } = useAuthStore();
  const widget = widgetRegistry.get(widgetId);

  // Check if widget exists
  if (!widget) {
    return (
      <Card className={`widget-error w-full ${className}`}>
        <CardContent className="flex items-center justify-center p-6">
          <div className="text-center">
            <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Widget not found: {widgetId}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Check if user has permission to view this widget
  if (!widgetRegistry.isWidgetAvailableForUser(widgetId, user)) {
    return null; // Don't render widgets user can't access
  }

  const widgetSize = size || widget.size;
  // All widgets are full width now - no column spanning needed
  const sizeClasses = {
    small: '',
    medium: '',
    large: '',
    full: ''
  };

  const content = (
    <Suspense fallback={<WidgetLoadingState />}>
      <WidgetRenderer
        widget={widget}
        role={role}
        onAction={onAction}
        widgetId={widgetId}
      />
    </Suspense>
  );

  // When showHeader is false the inner widget provides its own Card — no wrapper needed
  if (!showHeader) {
    return <div className={`widget-container w-full ${className}`}>{content}</div>;
  }

  return (
    <Card className={`widget-container w-full ${sizeClasses[widgetSize]} ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {widget.icon && <span className="text-muted-foreground">{widget.icon}</span>}
            <CardTitle className="text-lg font-semibold">{widget.title}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs capitalize">
              {widget.category}
            </Badge>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {widget.description && (
          <p className="text-sm text-muted-foreground">{widget.description}</p>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {content}
      </CardContent>
    </Card>
  );
}

/**
 * Internal component that renders the actual widget with error boundaries
 */
function WidgetRenderer({ widget, role, onAction, widgetId }: {
  widget: any;
  role?: RoleName | null;
  onAction?: (action: string, data?: any) => void;
  widgetId: string;
}) {
  const WidgetComponent = widget.component;

  // Error boundary wrapper
  try {
    return (
      <WidgetComponent
        role={role}
        onAction={onAction}
        widgetId={widgetId}
      />
    );
  } catch (error) {
    console.error(`Error rendering widget ${widgetId}:`, error);
    return <WidgetErrorState error={error} />;
  }
}

/**
 * Loading state for widgets
 */
function WidgetLoadingState() {
  return (
    <div className="flex items-center justify-center py-8">
      <LoadingSpinner size="sm" />
      <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
    </div>
  );
}

/**
 * Error state for widgets
 */
function WidgetErrorState({ error }: { error?: any }) {
  return (
    <div className="flex items-center justify-center py-8">
      <div className="text-center">
        <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
        <p className="text-sm text-muted-foreground mb-2">
          Failed to load widget
        </p>
        {error && (
          <p className="text-xs text-muted-foreground">
            {error.message || 'Unknown error'}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Hook to get available widgets for current user
 */
export function useAvailableWidgets() {
  const { user } = useAuthStore();
  return widgetRegistry.getWidgetsForUser(user);
}

/**
 * Hook to check if a specific widget is available
 */
export function useWidgetAvailable(widgetId: string) {
  const { user } = useAuthStore();
  return widgetRegistry.isWidgetAvailableForUser(widgetId, user);
}